import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RefreshIcon from "@mui/icons-material/Refresh";

const MIN = 1;
const MAX = 6;
const clamp = (s) => Math.min(MAX, Math.max(MIN, s));

// Visualizador de imagem em tela cheia com zoom (scroll/duplo clique/botões) e
// arrastar (pan) quando ampliado. Entrada: clique na miniatura. Saída: botão X,
// tecla ESC ou clique no fundo escuro.
const ImageViewer = ({ src, onClose }) => {
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  const drag = useRef({ on: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const zoomAt = useCallback((factor, cx, cy) => {
    const { scale, x, y } = viewRef.current;
    const next = clamp(scale * factor);
    if (next === scale) return;
    if (next === MIN) {
      setView({ scale: 1, x: 0, y: 0 });
      return;
    }
    const ratio = next / scale;
    // mantém o ponto (cx,cy), relativo ao centro, fixo sob o cursor
    setView({ scale: next, x: cx - (cx - x) * ratio, y: cy - (cy - y) * ratio });
  }, []);

  const zoomCenter = (factor) => zoomAt(factor, 0, 0);

  const reset = () => setView({ scale: 1, x: 0, y: 0 });

  // wheel precisa de listener nativo não-passivo p/ dar preventDefault
  useEffect(() => {
    const el = containerRef.current;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      zoomAt(e.deltaY < 0 ? 1.18 : 1 / 1.18, cx, cy);
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    el?.addEventListener("wheel", onWheel, { passive: false });
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      el?.removeEventListener("wheel", onWheel);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [zoomAt, onClose]);

  const onPointerDown = (e) => {
    if (viewRef.current.scale <= 1) return;
    drag.current = {
      on: true,
      moved: false,
      sx: e.clientX,
      sy: e.clientY,
      ox: viewRef.current.x,
      oy: viewRef.current.y,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.on) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    setView((v) => ({ ...v, x: d.ox + dx, y: d.oy + dy }));
  };
  const onPointerUp = () => {
    drag.current.on = false;
  };

  const onDoubleClick = (e) => {
    if (viewRef.current.scale > 1) return reset();
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;
    zoomAt(2.5, cx, cy);
  };

  const onBackdropClick = (e) => {
    // fecha só ao clicar no fundo (não na imagem) e sem ter arrastado
    if (e.target === containerRef.current && !drag.current.moved) onClose();
  };

  const zoomed = view.scale > 1;

  // Portal no body: o overlay precisa escapar do stacking context dos balões
  // (senão o painel/sidebar pintam por cima e o fundo escuro "vaza").
  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      onClick={onBackdropClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 13000,
        backgroundColor: "rgba(0,0,0,0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        touchAction: "none",
      }}
    >
      {/* Barra de controles */}
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          display: "flex",
          gap: 4,
          zIndex: 2,
        }}
      >
        <IconButton onClick={() => zoomCenter(1 / 1.4)} style={{ color: "#fff" }} title="Diminuir zoom">
          <ZoomOutIcon />
        </IconButton>
        <IconButton onClick={() => zoomCenter(1.4)} style={{ color: "#fff" }} title="Aumentar zoom">
          <ZoomInIcon />
        </IconButton>
        <IconButton onClick={reset} style={{ color: "#fff" }} title="Tamanho original">
          <RefreshIcon />
        </IconButton>
        <IconButton onClick={onClose} style={{ color: "#fff" }} title="Fechar (ESC)">
          <CloseIcon />
        </IconButton>
      </div>

      <img
        src={src}
        alt="imagem"
        draggable={false}
        onDoubleClick={onDoubleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          maxWidth: "92vw",
          maxHeight: "88vh",
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transformOrigin: "center center",
          transition: drag.current.on ? "none" : "transform 0.12s ease-out",
          cursor: zoomed ? (drag.current.on ? "grabbing" : "grab") : "zoom-in",
          userSelect: "none",
          willChange: "transform",
        }}
      />

      {/* Instruções de uso */}
      <div
        style={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.55)",
          color: "#eee",
          padding: "6px 14px",
          borderRadius: 20,
          fontSize: "0.8rem",
          display: "flex",
          gap: 14,
          flexWrap: "wrap",
          justifyContent: "center",
          maxWidth: "94vw",
          pointerEvents: "none",
        }}
      >
        <span>🖱️ Role para dar zoom</span>
        <span>✋ Arraste para mover</span>
        <span>👆 Duplo clique aproxima</span>
        <span>⎋ ESC ou clique fora fecha</span>
      </div>
    </div>,
    document.body
  );
};

export default ImageViewer;
